import type { ApolloClient, MutationUpdaterFn } from '@apollo/client';

import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import { mergeItemIntoListById } from 'src/modules/utils';
import * as Schema from 'src/types/graphql';

import { getCandidateIdFromCache } from '../data';

const updateLanguageCache = ({
  cache,
  languageProficiencies,
}: {
  cache: Parameters<MutationUpdaterFn>[0];
  languageProficiencies: Schema.LanguageProficiency[];
}) => {
  const _id = getCandidateIdFromCache(cache);

  cache.modify({
    id: cache.identify({
      __typename: 'Candidate',
      _id,
    }),
    fields: {
      languageProficiencies: () => languageProficiencies,
    },
  });
};

const getLanguageDataFromStore = ({
  cache,
}: {
  cache: Parameters<MutationUpdaterFn>[0];
}) => {
  const data = getDataFromCache({
    cache,
    query: Schema.GetLanguageProficienciesDocument,
  });

  return data?.viewer?.languageProficiencies || [];
};

export const updateCacheUpdateLanguage: MutationUpdaterFn<
  Schema.UpdateLanguageProficiencyMutation
> = (cache, { data }) => {
  const { updateLanguageProficiency } = data || {};

  // Refresh store with server data
  if (updateLanguageProficiency?.viewer?.languageProficiencies) {
    const { languageProficiencies } = updateLanguageProficiency.viewer;
    updateLanguageCache({ cache, languageProficiencies });
  }
};

export const updateCacheDeleteLanguage: MutationUpdaterFn<
  Schema.DeleteLanguageProficiencyMutation
> = (cache, { data }) => {
  const { deleteLanguageProficiency } = data || {};

  // Refresh store with server data
  if (deleteLanguageProficiency?.viewer?.languageProficiencies) {
    const { languageProficiencies } = deleteLanguageProficiency.viewer;
    updateLanguageCache({ cache, languageProficiencies });
  }
};

export const generateUpdateLanguageOptimisticResponse =
  (apolloClient: ApolloClient<any>) =>
  ({
    updateLanguageProficiencyInput,
  }: {
    updateLanguageProficiencyInput: Schema.UpdateLanguageProficiencyInput;
  }) => {
    const languages = getLanguageDataFromStore(apolloClient);

    const existingLanguage = languages.find(
      (language) => language.id === updateLanguageProficiencyInput.id,
    );

    let languageProficiencies: Schema.LanguageProficiency[] = [];
    let languageId = updateLanguageProficiencyInput.id;

    if (!existingLanguage) {
      const languageWithSameOntologyId = languages.find(
        (language) =>
          language.name.ontologyId ===
          updateLanguageProficiencyInput.name.ontologyId,
      );

      if (languageWithSameOntologyId) {
        languageId = languageWithSameOntologyId.id;
      }
    }

    languageProficiencies = [
      ...mergeItemIntoListById({
        list: languages,
        item: {
          __typename: 'LanguageProficiency',
          id: languageId,
          name: {
            __typename: 'OntologyStructuredData',
            ...updateLanguageProficiencyInput.name,
          },
        },
      }),
    ];

    return {
      __typename: 'Mutation',
      updateLanguageProficiency: {
        __typename: 'UpdateLanguageProficiencyPayload',
        viewer: {
          __typename: 'Candidate',
          languageProficiencies,
        },
      },
    };
  };

export const generateDeleteLanguageOptimisticResponse =
  (apolloClient: ApolloClient<any>) =>
  ({
    deleteLanguageProficiencyInput,
  }: {
    deleteLanguageProficiencyInput: Schema.DeleteLanguageProficiencyInput;
  }) => {
    const languages = getLanguageDataFromStore(apolloClient);

    return {
      __typename: 'Mutation',
      deleteLanguageProficiency: {
        __typename: 'DeleteLanguageProficiencyPayload',
        viewer: {
          __typename: 'Candidate',
          languageProficiencies: languages.filter(
            (language) => language.id !== deleteLanguageProficiencyInput.id,
          ),
        },
      },
    };
  };
